import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { saveDocumentSnapshot } from '@/lib/documentSave';

const Context = createContext(null);
const DOCUMENT_TOOLS = new Set(['/viewer', '/merge', '/split', '/compress', '/sign', '/protect', '/convert', '/watermark', '/page-numbers', '/flatten', '/crop', '/redact', '/ocr', '/delete', '/rotate']);

// One in-memory working document. Switching tools never writes to disk or
// uploads to cloud. The editor supplies its canonical save pipeline so every
// handoff includes text, images, annotations and filled form values.
export function DocumentWorkspaceProvider({ children }) {
  const routerNavigate = useNavigate();
  const location = useLocation();
  const [document, setDocument] = useState(() => location.state?.file ? { ...location.state } : null);
  const documentRef = useRef(document);
  const exporter = useRef(null);
  const replayingHistory = useRef(false);
  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [replacement, setReplacement] = useState(null);
  const [toolDirty, setToolDirty] = useState(false);
  const toolDirtyRef = useRef(toolDirty);
  toolDirtyRef.current = toolDirty;

  const remember = useCallback((next) => {
    documentRef.current = next;
    setDocument(next);
  }, []);

  const navigate = useCallback((to, options = {}) => {
    const destination = typeof to === 'string' ? to.split('?')[0] : null;
    if (to === '/viewer' && location.pathname === '/viewer' && !options.state?.file) return;
    if (to === '/viewer?panel=pages' && location.pathname === '/viewer' && !options.state?.file) {
      // This is an editor panel, not a document handoff. Pushing File objects
      // through browser history clones them, which would reload the editor.
      window.dispatchEvent(new Event('scrivar:organize-pages'));
      return;
    }
    const finish = (snapshot) => {
      let current = snapshot || documentRef.current;
      const openingAnother = options.state?.file && options.state.file !== current?.file &&
        (options.state.newDocument || !DOCUMENT_TOOLS.has(location.pathname));
      if (openingAnother && current?.dirty && !options.allowReplacement) {
        setReplacement({ current, to, options });
        return;
      }
      if (options.state?.file) {
        const continuing = current?.file && destination === '/viewer' && DOCUMENT_TOOLS.has(location.pathname) &&
          location.pathname !== '/viewer' && location.pathname !== '/flatten' && !options.state.newDocument &&
          !(location.pathname === '/convert' && location.search.includes('tab=to-pdf'));
        // Applying a tool updates the working document, including its save
        // destination. Export buttons on tool result screens still name copies.
        current = continuing
          ? { ...current, ...options.state, file: new File([options.state.file], current.file.name, { type: 'application/pdf' }), dirty: true, newDocument: false }
          : { ...options.state, dirty: options.state.justSaved ? false : (options.state.dirty ?? (destination === '/viewer' && DOCUMENT_TOOLS.has(location.pathname) && location.pathname !== '/viewer')) };
        remember(current);
      }
      const acceptsDocument = DOCUMENT_TOOLS.has(destination) &&
        !(destination === '/convert' && String(to).includes('tab=to-pdf'));
      const nextOptions = acceptsDocument && current
        ? { ...options, state: { ...current, ...(options.state?.file ? {} : options.state) } }
        : options;
      routerNavigate(to, nextOptions);
    };
    // In-page parameter changes and save finalization must not re-enter the
    // save pipeline. File replacement is handled by the editor's save path.
    if (!exporter.current || destination === '/viewer' && location.pathname === '/viewer' && !options.state?.newDocument) {
      finish();
      return;
    }
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    return Promise.resolve().then(() => exporter.current?.()).then((snapshot) => {
      if (snapshot) remember(snapshot);
      finish(snapshot);
    }).catch((err) => {
      setError(`Your document is still open. Could not prepare it for another tool: ${err.message}`);
    }).finally(() => {
      busyRef.current = false;
      setBusy(false);
    });
  }, [routerNavigate, location.pathname, location.search, remember]);

  const registerExporter = useCallback((getDocument) => {
    exporter.current = getDocument;
    return () => { if (exporter.current === getDocument) exporter.current = null; };
  }, []);

  useEffect(() => {
    // Browser Back/Forward bypass navigate(). Snapshot before letting the
    // router unmount the editor, then replay the same history entry with the
    // latest working file. Preserve the router's own history index and key.
    const onPop = (event) => {
      if (replayingHistory.current || !documentRef.current || busyRef.current) return;
      event.stopImmediatePropagation();
      const historyState = event.state;
      const destination = window.location.pathname;
      const previousUrl = location.pathname + location.search;
      busyRef.current = true; setBusy(true);
      const getDocument = exporter.current;
      Promise.resolve().then(() => getDocument?.() || documentRef.current).then((snapshot) => {
        if (snapshot) remember(snapshot);
        const accepts = DOCUMENT_TOOLS.has(destination) && !(destination === '/convert' && window.location.search.includes('tab=to-pdf'));
        const state = accepts && snapshot ? { ...historyState, usr: { ...snapshot } } : historyState;
        window.history.replaceState(state, '', window.location.href);
        replayingHistory.current = true;
        try { window.dispatchEvent(new PopStateEvent('popstate', { state })); }
        finally { replayingHistory.current = false; }
      }).catch((err) => {
        window.history.replaceState({ ...historyState, usr: location.state }, '', previousUrl);
        setError(`Could not prepare your document: ${err.message}. Your edits are still open.`);
      }).finally(() => { busyRef.current = false; setBusy(false); });
    };
    window.addEventListener('popstate', onPop, true);
    return () => window.removeEventListener('popstate', onPop, true);
  }, [location, remember]);

  const openReplacement = async (saveFirst) => {
    const pending = replacement;
    if (!pending) return;
    setBusy(true);
    try {
      if (saveFirst) {
        const saved = await saveDocumentSnapshot(pending.current);
        if (!saved) return;
        remember(saved);
      }
      setReplacement(null);
      await navigate(pending.to, { ...pending.options, allowReplacement: true });
    } catch (err) { setError(`Could not save your current document: ${err.message}`); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    // Marketing navigation still uses anchors. Internal application links
    // must go through the same handoff instead of reloading and losing RAM.
    const onClick = (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target.closest?.('a[href]');
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (!DOCUMENT_TOOLS.has(url.pathname) && !['/tools', '/account', '/account/billing', '/cloud', '/recent'].includes(url.pathname)) return;
      event.preventDefault();
      navigate(url.pathname + url.search);
    };
    window.document.addEventListener('click', onClick);
    return () => window.document.removeEventListener('click', onClick);
  }, [navigate]);

  useEffect(() => {
    // Keep a working copy protected even while the editor is unmounted.
    const handler = (event) => {
      if (!(documentRef.current?.dirty || toolDirtyRef.current) || window.electronAPI) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  useEffect(() => {
    if (location.pathname === '/viewer') return;
    window.__scrivarHasUnsavedChanges = Boolean(documentRef.current?.dirty || toolDirty);
    const api = window.electronAPI;
    if (!api?.onSaveAndClose) return;
    const save = async (intent = 'save') => {
      let current = documentRef.current;
      if (!current || busyRef.current) return false;
      busyRef.current = true; setBusy(true);
      try {
        // Tools with pending operations must prepare or reject the save before
        // Save and Close can acknowledge success for an older working file.
        const snapshot = await exporter.current?.();
        if (snapshot) { remember(snapshot); current = snapshot; }
        const saved = await saveDocumentSnapshot(current, { intent });
        if (!saved) return false;
        if (documentRef.current === current) remember(saved);
        return documentRef.current === saved;
      } catch (err) { setError(`Could not save: ${err.message}. Your document is still open.`); return false; }
      finally { busyRef.current = false; setBusy(false); }
    };
    const cleanupClose = api.onSaveAndClose(async () => api.notifySaveComplete(await save()));
    const cleanupSave = api.onMenuSave?.(() => save());
    const cleanupSaveAs = api.onMenuSaveAs?.(() => save('saveAs'));
    return () => { cleanupClose?.(); cleanupSave?.(); cleanupSaveAs?.(); };
  }, [location.pathname, document, remember, toolDirty]);

  return <Context.Provider value={{ document, remember, navigate, registerExporter, setToolDirty, busy }}>
    {children}
    {replacement && <div className="fixed inset-0 z-[110] bg-gray-900/40 flex items-center justify-center p-4">
      <section role="dialog" aria-modal="true" aria-label="Save your current document" className="bg-white rounded-2xl p-6 shadow-xl max-w-lg">
        <h2 className="text-lg font-semibold text-gray-900">Save your current document?</h2>
        <p className="mt-2 text-sm text-gray-600">{replacement.current.file.name} has unsaved changes. Save it before opening {replacement.options.state.file.name}.</p>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button disabled={busy} className="px-3 py-2 text-sm" onClick={() => setReplacement(null)}>Keep editing</button>
          <button disabled={busy} className="px-3 py-2 text-sm text-red-600" onClick={() => openReplacement(false)}>Discard and open</button>
          <button disabled={busy} className="px-3 py-2 text-sm rounded-lg bg-brand-blue text-white" onClick={() => openReplacement(true)}>Save and open</button>
        </div>
      </section>
    </div>}
    {busy && <div role="status" className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[100] bg-gray-900 text-white px-5 py-3 rounded-xl shadow-lg">Preparing your document…</div>}
    {error && <div role="alert" className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[100] bg-red-50 text-red-800 border border-red-200 px-5 py-3 rounded-xl shadow-lg max-w-xl">{error}<button className="ml-3 underline" onClick={() => setError(null)}>Dismiss</button></div>}
  </Context.Provider>;
}

export function useDocumentWorkspace() { return useContext(Context); }

export function useRememberDocument() {
  const workspace = useDocumentWorkspace();
  const location = useLocation();
  const current = useRef({ workspace, location });
  current.current = { workspace, location };
  return useCallback((file) => {
    const { workspace: context, location } = current.current;
    if (!context || context.document?.file === file) return;
    context.remember(file === location.state?.file ? { ...location.state, file } : { file, dirty: false });
  }, []);
}

export function useWorkspaceNavigate() {
  const workspace = useDocumentWorkspace();
  const navigate = useNavigate();
  return workspace?.navigate || navigate;
}

// Load a handed-off document once. Loader identity often changes when pdfDoc
// is set; it must never trigger a second load (or duplicate Merge entries).
export function useIncomingDocument(loadFile) {
  const location = useLocation();
  const seen = useRef(null);
  const loader = useRef(loadFile);
  loader.current = loadFile;
  useEffect(() => {
    const file = location.state?.file;
    if (!file || file === seen.current) return;
    seen.current = file;
    loader.current(file);
  }, [location.state?.file]);
}
